import { Link } from 'react-router-dom';
import { melbourneDate } from '../../../shared/dates';
import { BRANDS, SOCIAL_ASSET_TYPES, type SocialAssetWithProject } from '../../../shared/types';
import { BrandBadge, formatDate, formatDayMonth, TrashAction } from '../../components/primitives';
import { assetChannels, channelLabel, filterAssets, restrictionSource, safeUrl, shortUrl, STATUS_FILTER_ORDER, STATUS_LABELS, TYPE_LABELS, type SocialActions } from '../../lib/socialCalendar';
import { StatusPill, FilterChip } from './chrome';
import { type SocialLibraryProps } from './types';

function RowAction({
  asset,
  actions,
}: {
  asset: SocialAssetWithProject;
  actions: SocialActions;
}) {
  const stop = (fn: () => void) => (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    fn();
  };
  if (asset.effective_restricted) {
    return restrictionSource(asset) === 'project' ? (
      <button type="button" className="chip" onClick={stop(() => actions.onOpenAsset(asset.id))}>
        view note
      </button>
    ) : (
      <button type="button" className="chip" onClick={stop(() => actions.onUnrestrict(asset))}>
        unrestrict
      </button>
    );
  }
  switch (asset.status) {
    case 'ready':
      return (
        <button type="button" className="chip accent" onClick={stop(() => actions.onStartSchedule(asset))}>
          schedule
        </button>
      );
    case 'needs_prep':
      return (
        <button type="button" className="chip" onClick={stop(() => actions.onMarkReady(asset))}>
          mark ready
        </button>
      );
    case 'scheduled':
      return (
        <button type="button" className="chip" onClick={stop(() => actions.onUnschedule(asset))}>
          unschedule
        </button>
      );
    default:
      return (
        <button type="button" className="chip" onClick={stop(() => actions.onRepurpose(asset))}>
          repurpose
        </button>
      );
  }
}

function AssetDrawer({
  asset,
  actions,
}: {
  asset: SocialAssetWithProject;
  actions: SocialActions;
}) {
  const source = restrictionSource(asset);
  const href = safeUrl(asset.url);
  if (source !== null) {
    return (
      <div className="social-drawer note-only" onClick={(e) => e.stopPropagation()}>
        <div className="social-note">
          <div className="nl">
            {source === 'project'
              ? `restricted · project flag · ${asset.project_name ?? 'project'} · no social use`
              : 'restricted · this asset'}
          </div>
          {(source === 'project' ? asset.project_social_restricted_note : asset.restricted_note)
            ?? 'No note recorded.'}
          {source === 'project' && asset.project_id !== null && (
            <div style={{ marginTop: 8 }}>
              <Link className="group-link" to={`/projects/${asset.project_id}`}>
                Clear the flag on the project →
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="social-drawer" onClick={(e) => e.stopPropagation()}>
      <div className="social-dcol">
        <div className="dl">Link</div>
        <div className="dv">
          {href !== null ? (
            <a href={href} target="_blank" rel="noreferrer" title={href}>
              {shortUrl(href)}
            </a>
          ) : asset.url ? (
            // Not a web address: show what is stored so it can be fixed,
            // but never as something clickable.
            <span className="dim" title={asset.url}>
              {asset.url} · not a web link
            </span>
          ) : (
            '—'
          )}
        </div>
      </div>
      <div className="social-dcol">
        <div className="dl">Source</div>
        <div className="dv">
          {asset.project_id !== null ? (
            <Link to={`/projects/${asset.project_id}`}>{asset.project_name} →</Link>
          ) : (
            'Standalone'
          )}
        </div>
      </div>
      <div className="social-dcol">
        <div className="dl">Note</div>
        <div className="dv" title={asset.note ?? undefined}>
          {asset.note ?? '—'}
        </div>
      </div>
      <div className="social-dcol">
        <div className="dl">Added</div>
        <div className="dv">{formatDate(melbourneDate(asset.created_at))}</div>
      </div>
      <div className="social-dcol" style={{ marginLeft: 'auto' }}>
        <div className="dl">Actions</div>
        <div className="dv social-drawer-actions">
          <button type="button" className="chip" onClick={() => actions.onEdit(asset)}>
            edit
          </button>
          <button type="button" className="chip danger" onClick={() => actions.onRestrict(asset)}>
            restrict
          </button>
          <TrashAction label="delete" onConfirm={() => actions.onDelete(asset)} />
        </div>
      </div>
    </div>
  );
}

export function SocialLibraryBody({
  assets,
  filters,
  onFilter,
  openId,
  actions,
  popover,
}: SocialLibraryProps) {
  const rows = filterAssets(assets, filters);
  return (
    <div data-social-view="library">
      <div className="social-filters">
        {STATUS_FILTER_ORDER.map((status) => (
          <FilterChip
            key={status}
            label={STATUS_LABELS[status]}
            on={filters.status === status}
            onClick={() => onFilter({ ...filters, status: filters.status === status ? null : status })}
          />
        ))}
        <span className="social-filter-sep">|</span>
        {BRANDS.map((brand) => (
          <FilterChip
            key={brand}
            label={brand}
            on={filters.brand === brand}
            onClick={() => onFilter({ ...filters, brand: filters.brand === brand ? null : brand })}
          />
        ))}
        <span className="social-filter-sep">|</span>
        {SOCIAL_ASSET_TYPES.map((type) => (
          <FilterChip
            key={type}
            label={TYPE_LABELS[type]}
            on={filters.type === type}
            onClick={() => onFilter({ ...filters, type: filters.type === type ? null : type })}
          />
        ))}
        <button type="button" className="chip accent social-add" onClick={actions.onAdd}>
          + add asset
        </button>
      </div>

      <div className="social-rowbox">
        {rows.map((asset) => (
          <div key={asset.id}>
            <div
              className={`row social-row${asset.effective_restricted ? ' restricted' : ''}`}
              onClick={() => actions.onOpenAsset(asset.id)}
            >
              <span className="pill social-type">{TYPE_LABELS[asset.type]}</span>
              <span className="row-title" title={asset.label}>
                {asset.label}
              </span>
              <span className="row-context">· {asset.project_name ?? 'standalone'}</span>
              <span className="row-spacer" />
              {asset.planned_date !== null && !asset.effective_restricted && (
                <span className="row-meta dim">
                  {channelLabel(assetChannels(asset.channels))} · {formatDayMonth(asset.planned_date)}
                </span>
              )}
              <BrandBadge brand={asset.brand} />
              <StatusPill asset={asset} />
              <RowAction asset={asset} actions={actions} />
            </div>
            {openId === asset.id && <AssetDrawer asset={asset} actions={actions} />}
          </div>
        ))}
        {rows.length === 0 && <div className="empty-note">Nothing matches these filters.</div>}
      </div>

      {popover}
    </div>
  );
}
